import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, FileText, Shield } from 'lucide-react';
import { LogoPublic } from '../components/Logo';
import api, { billingAPI, markLegalJustAccepted } from '../lib/api';
import { toast } from 'sonner';

const TERMS_URL = 'https://stockandload.com/terms-of-service/';
const PRIVACY_URL = 'https://stockandload.com/privacy-policy/';

export default function LegalAccept() {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setAuth, logout } = useAuth();
  const navigate = useNavigate();

  const handleAccept = async () => {
    if (!accepted) return;
    setLoading(true);
    try {
      const response = await api.post('/legal/accept', {
        accept_terms: true,
        accept_privacy: true,
      });
      const { access_token, refresh_token, user } = response.data;

      // Mark legal as just accepted to suppress stale-request 403 redirects
      markLegalJustAccepted();

      await setAuth({
        token: access_token,
        refreshToken: refresh_token,
        user,
      });

      toast.success('Terms accepted');

      // Evaluate subscription status before redirecting
      // This prevents hitting ProtectedRoute which could trigger redirect cascades
      try {
        const billingRes = await billingAPI.getBetaTrialStatus();
        const { has_active_subscription } = billingRes.data;
        if (!has_active_subscription) {
          navigate('/billing', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch {
        // If billing check fails, default to /billing to avoid loops
        navigate('/billing', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept terms');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="legal-accept-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <LogoPublic size="lg" className="mb-2" />
        </div>

        <Card className="border-border shadow-sm">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold font-heading">Updated Terms</CardTitle>
            <CardDescription>
              We&apos;ve updated our legal documents. Please review and accept to continue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                data-testid="legal-terms-link"
              >
                <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Terms of Service</p>
                  <p className="text-xs text-muted-foreground">Read our terms of service</p>
                </div>
              </a>
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                data-testid="legal-privacy-link"
              >
                <Shield className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Privacy Policy</p>
                  <p className="text-xs text-muted-foreground">Read our privacy policy</p>
                </div>
              </a>
            </div>

            <div className="flex items-start gap-3 rounded-lg bg-muted/50 p-3">
              <Checkbox
                id="accept-legal"
                checked={accepted}
                onCheckedChange={(checked) => setAccepted(!!checked)}
                data-testid="legal-accept-checkbox"
              />
              <label htmlFor="accept-legal" className="text-sm leading-snug cursor-pointer select-none">
                I have read and agree to the{' '}
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                  Privacy Policy
                </a>
              </label>
            </div>

            <Button
              onClick={handleAccept}
              disabled={!accepted || loading}
              className="w-full h-12 text-base font-bold btn-press"
              data-testid="legal-accept-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                'Accept & Continue'
              )}
            </Button>

            <button
              onClick={logout}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="legal-logout-link"
            >
              Sign out instead
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
